import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Iconify from "../../components/iconify";
import SetupsTable from "../../components/SetupsTable";
import { useNavigate } from "react-router-dom";
import { del, get } from "src/utils/networkUtils";

export default function ChallengeSetupsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [challengeTypeFilters, setChallengeTypeFilters] = useState({
    public: true,
    club: true,
  });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const limit = 25;

  const fetchClubs = useCallback(async (currentOffset = 0) => {
    setLoading(true);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', currentOffset.toString());

      // Add challengeType filter
      const publicChecked = challengeTypeFilters.public;
      const clubChecked = challengeTypeFilters.club;

      if (publicChecked && !clubChecked) {
        queryParams.append('challengeType', 'public');
      } else if (clubChecked && !publicChecked) {
        queryParams.append('challengeType', 'club');
      }
      // If both are checked or both unchecked, don't send challengeType parameter

      const resp = await get(`/admin/challenge-setups?${queryParams.toString()}`);

      if (resp.success) {
        setRows((prev) =>
          currentOffset === 0
            ? resp.data
            : [...prev, ...resp.data]
        );

        setHasMore(resp.data.length === limit);
      }
    } catch (error) {
      console.error("Error fetching clubs:", error);
    } finally {
      setLoading(false);
    }
  }, [challengeTypeFilters, limit]);

  // Reset pagination when filters change
  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(true);
  }, [challengeTypeFilters]);

  useEffect(() => {
    fetchClubs(offset);
  }, [offset, fetchClubs]);

  useEffect(() => {
    if (offset === 0) {
      fetchClubs(0);
    }
  }, [offset, challengeTypeFilters, fetchClubs]);

  const lastClubRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          console.log('Loading more clubs...', { currentOffset: offset, hasMore });
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, offset]
  );

  // Fallback scroll detection in case SetupsTable doesn't support ref
  useEffect(() => {
    const handleScroll = () => {
      if (loading || !hasMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.offsetHeight;

      if (scrollTop + windowHeight >= docHeight - 100) {
        console.log('Scroll pagination trigger...', { offset, hasMore });
        setOffset((prev) => prev + limit);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, offset, limit]);

  const handleChallengeTypeFilterChange = (type) => {
    setChallengeTypeFilters({
      ...challengeTypeFilters,
      [type]: !challengeTypeFilters[type],
    });
  };

  const handleEdit = (clubId, clubData) => {
    navigate(`/dashboard/challenge-setup/${clubId}`, { state: { clubData } });
  };

  const handleViewChallenges = (setupId) => {
    navigate(`/dashboard/challenges?setupId=${setupId}`);
  };

  const handleDeleteClub = async (clubId) => {
    setDeleteLoading(true);
    try {
      await del(`/admin/challenge-setups/${clubId}`);
      // Reset pagination and refresh data after successful deletion
      setOffset(0);
      setRows([]);
      setHasMore(true);
      await fetchClubs(0);
    } catch (e) {
      console.error("Error deleting club:", e);
      // You could add error toast/notification here if needed
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Challenges - Setups</title>
      </Helmet>

      <Container>
        {/* Header Section */}
        <Box
          sx={{
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#667eea" }}>
              Challenges - Setups
            </Typography>
          </Stack>
        </Box>

        {/* Filter Controls */}
        <Card sx={{ mb: 3, p: 2, background: "none", boxShadow: "none" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Stack direction="row" spacing={2}>
                {Object.entries(challengeTypeFilters).map(([type, checked]) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={loading}
                        onChange={() => handleChallengeTypeFilterChange(type)}
                        sx={{
                          color: "#667eea",
                          "&.Mui-checked": {
                            color: "#667eea",
                          },
                        }}
                      />
                    }
                    label={type === 'public' ? 'Public' : 'Club'}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: 500,
                        opacity: loading ? 0.5 : 1,
                        textTransform: 'capitalize',
                      },
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => navigate("/dashboard/challenges")}
                variant="outlined"
              >
                View all Challenges
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-outline" />}
                onClick={() => navigate("/dashboard/challenge-setup/create")}
              >
                Add New Setup
              </Button>
            </Stack>
          </Stack>
        </Card>

        {/* Table */}
        <Card>
          <SetupsTable
            loading={loading && rows?.length === 0}
            deleteLoading={deleteLoading}
            rows={rows}
            onEdit={handleEdit}
            onViewChallenges={handleViewChallenges}
            onDelete={handleDeleteClub}
            deleteText="Delete Setup"
            emptyMessage="No Club Found"
            lastRowRef={lastClubRef}
          />

          {((loading && rows?.length > 0) || deleteLoading) && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Card>
      </Container>
    </>
  );
}
